// The plan catalogue — every commercially binding fact about a WeConsultRX
// plan, in one place, because it was in four.
//
// ─── WHAT WENT WRONG ────────────────────────────────────────────────────────
//
// Before this file, the same offer was declared independently in the API, the
// web client, the in-app Billing page and the marketing site, and no two were
// derived from each other. `/pricing` advertised "Professional · 4–10
// facilities · volume pricing" against flat per-unit Stripe prices with no
// taper — a discount no customer had ever received. Nothing detected it.
//
// THE MARKETING SITE IMPORTS THIS FILE. That is the entire reason this package
// has no dependencies: `apps/landing` is statically prerendered and cannot
// carry the Bedrock and Supabase SDKs that reach it through
// @weconsultrx/claude-synthesis.
//
// ─── THE SHAPE OF THE OFFER (2026-08-29) ────────────────────────────────────
//
// Two FAMILIES, priced PER FACILITY on a graduated scale, plus `trial`.
//
//   ambulatory  ASC + Dialysis + Custom. $50 first facility, $40 each after.
//   ltc         SNF + ALF, AND everything ambulatory. $100 / $75.
//
// Annual is the monthly rate × ANNUAL_MONTHS_CHARGED — one month free.
// 1–8 facilities is self-serve. 9+ is quoted, not listed.
//
// ─── WHY THE 1–4 / 5–8 BANDS ARE GONE ───────────────────────────────────────
//
// They shipped on 2026-08-27 with a FLAT price per band, and flat pricing
// inverted value capture: within a band every marginal facility was free, so a
// four-facility operator paid the same as a one-facility one for four times the
// work, four times the onboarding and four times the AI. The customers getting
// the most value paid the least per unit of it.
//
// Graduated per-facility pricing already prices the whole continuum, so bands
// on top of it would be incoherent — a 5th facility would re-charge the
// first-facility rate at the band edge. `ambulatory_small`/`_growth` and
// `ltc_small`/`_growth` therefore collapse into `ambulatory` and `ltc`. The old
// ids stay readable; see LEGACY_TIER_MIGRATION.
//
// EVERY TIER REQUIRES A BOOKED DEMO. There is no self-serve path onto this
// platform: a tenant needs a signed BAA and facility onboarding before it can
// hold PHI. `requiresDemo` is `true` for every tier so a future card cannot
// quietly reintroduce a direct-to-app link.

import {
  TOKENS_PER_SYNTHESIS_RUN,
  SYNTHESES_PER_RESIDENT_PER_MONTH,
} from "./allowance.js";

export type PlanFamily = "ambulatory" | "ltc";

export type PlanTier =
  | "trial"
  | "ambulatory"
  | "ambulatory_enterprise"
  | "ltc"
  | "ltc_enterprise";

/**
 * Tier names this catalogue has replaced, in the order they existed.
 *
 * `starter`/`pro`/`enterprise` came from the original per-facility model.
 * `*_small`/`*_growth` came from the flat-band model that lived for two days.
 * Live rows may hold any of them, so every read path must accept them.
 */
export type LegacyPlanTier =
  | "starter"
  | "pro"
  | "enterprise"
  | "ambulatory_small"
  | "ambulatory_growth"
  | "ltc_small"
  | "ltc_growth";

export type AnyPlanTier = PlanTier | LegacyPlanTier;

/**
 * Workflow modules, keyed to facility-type families. Defined here rather than
 * in the API because module entitlement is a PLAN fact: what the ambulatory
 * family buys is precisely "everything except `snf_alf`".
 */
export type ModuleKey = "snf_alf" | "asc" | "dialysis";

export const MODULE_KEYS: readonly ModuleKey[] = ["snf_alf", "asc", "dialysis"] as const;

export const MODULE_INFO: Record<
  ModuleKey,
  { label: string; facilityTypes: string; description: string }
> = {
  snf_alf: {
    label: "Long-term care",
    facilityTypes: "SNF, ALF",
    description:
      "Monthly medication regimen review, resident-level flags, GDR tracking, F-tag mapping, and the prescriber and DON signing loop.",
  },
  asc: {
    label: "Ambulatory surgery",
    facilityTypes: "ASC, Custom",
    description:
      "AAAHC-aligned rule packs, ASC forms and schedules, med-cart inspections, expiring stock, and formulary review.",
  },
  dialysis: {
    label: "Dialysis",
    facilityTypes: "Dialysis",
    description:
      "CfC-aligned dialysis safety rule packs, per-resident review, and the dialysis-specific reporting bundle.",
  },
};

/**
 * External-portal roles, matching `external_identities.role` exactly.
 *
 * The same string the portal keys off, so a pricing card cannot advertise a
 * portal provisioning has no role for — and so the invite route can gate on the
 * same list this file publishes.
 */
export type PortalRole = "facility_admin" | "facility_rep" | "provider";

export const PORTALS: Record<
  PortalRole,
  { role: PortalRole; label: string; description: string }
> = {
  facility_admin: {
    role: "facility_admin",
    label: "Facility Admin portal",
    description:
      "Site leadership sees coverage, forms, invoices, and contracts for their own facility.",
  },
  facility_rep: {
    role: "facility_rep",
    label: "DON portal",
    description:
      "The Director of Nursing reviews and signs nursing-audience recommendations and acknowledges bundles.",
  },
  provider: {
    role: "provider",
    label: "Provider portal",
    description:
      "Prescribers receive, respond to, and sign recommendation letters bound to their own roster.",
  },
};

/**
 * Which portals a family includes — NOT all three everywhere.
 *
 * An ASC has no Director of Nursing, and its rule pack is facility-level: the
 * work is forms, inspections, expiring stock and formulary review, none of
 * which produces a recommendation for a prescriber to sign. Shipping a DON and
 * a prescriber portal to an ambulatory customer advertises two surfaces nobody
 * there has a role for.
 *
 * ─── THE DIALYSIS CAVEAT, RECORDED DELIBERATELY ─────────────────────────────
 *
 * `dialysis` sits in the ambulatory family but is genuinely per-resident: its
 * rule pack is resident-scoped and the chart-review surfaces are tagged
 * `["snf_alf", "dialysis"]`. If a dialysis customer ever runs resident reviews,
 * the recommendations they produce have NO prescriber signing path under this
 * entitlement.
 *
 * A live limitation, not an oversight — decided 2026-08-29 with it stated.
 * Production has zero dialysis reviews, so nothing breaks today. If dialysis
 * review is ever sold, `provider` belongs back in this list, or dialysis needs
 * a family of its own.
 */
export const FAMILY_PORTALS: Record<PlanFamily, readonly PortalRole[]> = {
  ambulatory: ["facility_admin"],
  ltc: ["facility_admin", "facility_rep", "provider"],
};

const ALL_PORTAL_ROLES: readonly PortalRole[] = ["facility_admin", "facility_rep", "provider"];

/**
 * Portals a family entitles.
 *
 * A tenant with no family — `trial`, or an unrecognised tier — gets everything,
 * matching the fail-open posture of the module resolver: a prospect must be
 * able to see the surface they are evaluating, and an unknown tier must not
 * silently lock someone out of a portal they are paying for.
 */
export function portalsForFamily(family: PlanFamily | null): readonly PortalRole[] {
  if (!family) return ALL_PORTAL_ROLES;
  return FAMILY_PORTALS[family];
}

/** Whether a stored plan tier entitles a given portal role. */
export function planAllowsPortalRole(planTier: string | null | undefined, role: string): boolean {
  const tier = resolvePlanTier(planTier);
  return (portalsForFamily(PLAN_CATALOG[tier].family) as readonly string[]).includes(role);
}

/**
 * Tokens in one AI credit.
 *
 * Credits exist because "1,224,000 tokens" is not a number a pharmacist can
 * price against. TOKENS REMAIN WHAT IS ENFORCED — `ai_token_limit_monthly` is a
 * token column and the budget guards count tokens; credits are the unit
 * published and billed in.
 */
export const TOKENS_PER_CREDIT = 1_000;

export function creditsToTokens(credits: number): number {
  if (!Number.isFinite(credits) || credits <= 0) return 0;
  return Math.round(credits) * TOKENS_PER_CREDIT;
}

export function tokensToCredits(tokens: number): number {
  if (!Number.isFinite(tokens) || tokens <= 0) return 0;
  return tokens / TOKENS_PER_CREDIT;
}

/**
 * Months charged for a year. One month free.
 *
 * A separate Stripe Price on the same Product, not a coupon — a coupon can be
 * removed from a subscription and a Price cannot, so the discount stays
 * attached to what was actually sold.
 *
 * The sibling product on the same Stripe account (GovMatrixIQ) discounts annual
 * by 15%; this is 8.3%, the conservative end. Widening it later means creating
 * a new annual Price, which costs nothing — a Price is immutable, so it is
 * replaced rather than edited either way.
 */
export const ANNUAL_MONTHS_CHARGED = 11;

/**
 * How a tier's listed price is charged.
 *
 * "graduated_per_facility" — the first facility costs `first_facility_cents`,
 * every one after costs `additional_facility_cents`. The Stripe Price is
 * `billing_scheme: tiered`, `tiers_mode: graduated`, with the facility count as
 * the quantity.
 *
 * WHAT THIS REPLACED: a flat price per band, in which every facility after the
 * first was free inside its band. It shipped 2026-08-27 and was reversed
 * 2026-08-29 — before any Stripe Price existed and before any customer
 * subscribed, which is the only reason the reversal was free. A Stripe Price is
 * immutable; once a subscription exists, changing the billing model means
 * migrating live subscriptions rather than editing a constant.
 */
export const BILLING_UNIT = "graduated_per_facility" as const;

export interface PlanDefinition {
  tier: PlanTier;
  /** `null` only for `trial`, which is not sold and belongs to no family. */
  family: PlanFamily | null;
  label: string;
  tagline: string;
  /** Inclusive facility-count band. `max` is `Infinity` for the 9+ tiers. */
  facilities: { min: number; max: number };
  /**
   * Monthly price of the FIRST facility, in cents. `null` for "Contact us".
   *
   * `null` is load-bearing: `planIsSelfServe()` keys off it, and a tier with no
   * price must never mint a Checkout Session, because there is no agreed amount
   * to charge.
   */
  first_facility_cents: number | null;
  /** Monthly price of each facility after the first. `null` when quoted. */
  additional_facility_cents: number | null;
  /**
   * AI credits for clinical synthesis, PER FACILITY per month.
   *
   * Per facility, not per plan. Cost scales with residents, residents scale
   * with facilities, so an allowance that did not scale with facilities was the
   * flat model's margin problem restated as a quota.
   */
  ai_credits_per_facility: number;
  /** Assistant credits per facility. A SEPARATE pool from synthesis. */
  assistant_credits_per_facility: number;
  /**
   * Monitored residents ONE facility's allowance is sized for.
   *
   * For `ltc`, the assumed census of a nursing home. For `ambulatory` it
   * describes a DIALYSIS census only — ASC and custom facilities generate no
   * synthesis at all (facility-level rule pack, and the chart-review surfaces
   * are not tagged for them), so their real consumption is about zero.
   */
  residents_per_facility: number;
  modules: Record<ModuleKey, boolean>;
  /** Portals this family includes. Not all three everywhere. */
  portals: readonly PortalRole[];
  requiresDemo: true;
}

/**
 * AI CREDIT SIZING — measured, not assumed.
 *
 * Every figure below comes from `public.ai_syntheses` in production rather than
 * from a code comment. See TOKENS_PER_SYNTHESIS_RUN and
 * SYNTHESES_PER_RESIDENT_PER_MONTH for the queries and their date.
 *
 * At the measured $0.0395 per BILLED synthesis run, one facility's allowance
 * costs at absolute worst — a tenant consuming every credit it is granted:
 *
 *   family        credits/facility   ≈ runs   AI cost   1st facility   each after
 *   ambulatory                 500       81     $3.20     $50 (6.4%)   $40 (8.0%)
 *   ltc                      1,000      163     $6.44    $100 (6.4%)   $75 (8.6%)
 *
 * SCALE-INVARIANT, WHICH IS THE POINT. Under the flat model an eight-facility
 * long-term-care tenant paid $125 and could consume $93 of inference — 74% —
 * while a one-facility tenant on the same family paid $100 and consumed $6.
 * Pricing and allowance now scale together, so margin no longer depends on how
 * many facilities a customer happens to have.
 *
 * These are CEILINGS. Prod runs about one billed synthesis per resident per
 * review against an allowance sized for two, so expected spend is half of each.
 *
 * The one thing that would move them badly is the MODEL. $0.0395 is Sonnet; a
 * tenant pinned to Opus via `ANTHROPIC_MODEL_ID` or a per-tenant setting costs
 * roughly five times that, and nothing here knows about it.
 */
const AI_CREDIT_SIZING = {
  /** Monitored beds assumed per long-term-care facility. */
  LTC_BEDS_PER_FACILITY: 80,
  /** Monitored beds per DIALYSIS facility; ASC and custom generate none. */
  DIALYSIS_BEDS_PER_FACILITY: 40,
  /** Measured USD per BILLED synthesis run. */
  USD_PER_SYNTHESIS_RUN: 0.0395,
} as const;

export { AI_CREDIT_SIZING };

/**
 * Credits one facility needs to cover `residents` at the plan-design rate.
 *
 * Derived rather than declared, so the census a plan advertises and the credits
 * it grants cannot drift apart — the defect that once left every paid tier
 * under-provisioned. Rounded UP to a whole hundred so the published figure is
 * one a human would write down.
 */
export function creditsForResidents(residents: number): number {
  if (!Number.isFinite(residents) || residents <= 0) return 0;
  const tokens = residents * TOKENS_PER_SYNTHESIS_RUN * SYNTHESES_PER_RESIDENT_PER_MONTH;
  return Math.ceil(tokens / TOKENS_PER_CREDIT / 100) * 100;
}

/** Residents an allowance covers. The inverse of `creditsForResidents`. */
export function residentLimitForCredits(credits: number): number {
  const tokens = creditsToTokens(credits);
  const perResident = TOKENS_PER_SYNTHESIS_RUN * SYNTHESES_PER_RESIDENT_PER_MONTH;
  if (perResident <= 0) return 0;
  return Math.floor(tokens / perResident);
}

/**
 * The sentinel for an unbounded limit.
 *
 * Not `Infinity`: the column is `int not null`, and an infinite denominator
 * would render on the usage meter.
 */
export const UNLIMITED = 999_999;

/**
 * Is this stored limit the "no ceiling" SENTINEL rather than a quantity?
 *
 * Every consumer of a `*_limit` column must ask this explicitly, and lives here
 * — beside the sentinel — so the server gate and the Billing meter cannot drift
 * into two answers.
 *
 * Comparing against it as an ordinary number "works" for a gate (nobody has a
 * million facilities) and then renders "Facilities 12 / 999,999" on the meter,
 * which is the awkward half of the same mistake: the highest-value account on
 * the platform opens Billing and reads what looks like a configuration error
 * instead of the unlimited plan they are paying for.
 *
 * `>=`, not `===`, so a hand-set larger number is also unbounded rather than
 * becoming an absurd but finite ceiling nobody meant to impose.
 */
export function isUnlimitedLimit(limit: number | null | undefined): boolean {
  return typeof limit === "number" && limit >= UNLIMITED;
}

const AMBULATORY_MODULES: Record<ModuleKey, boolean> = {
  snf_alf: false,
  asc: true,
  dialysis: true,
};

/** Long-term care buys EVERYTHING, ambulatory included. */
const LTC_MODULES: Record<ModuleKey, boolean> = {
  snf_alf: true,
  asc: true,
  dialysis: true,
};

/** The self-serve ceiling. Past this the account is quoted, not listed. */
const SELF_SERVE_MAX_FACILITIES = 8;

export const PLAN_CATALOG: Record<PlanTier, PlanDefinition> = {
  trial: {
    tier: "trial",
    family: null,
    label: "Trial",
    tagline: "Evaluate the platform on one facility.",
    facilities: { min: 1, max: 1 },
    first_facility_cents: 0,
    additional_facility_cents: null,
    // A capped taste — ~12 synthesis runs. Deliberately NOT census-covering:
    // evaluating the product is possible, running a facility on it is not.
    ai_credits_per_facility: 75,
    assistant_credits_per_facility: 25,
    residents_per_facility: 25,
    modules: { ...LTC_MODULES },
    // A prospect must be able to see every surface they are evaluating.
    portals: ALL_PORTAL_ROLES,
    requiresDemo: true,
  },

  ambulatory: {
    tier: "ambulatory",
    family: "ambulatory",
    label: "Ambulatory",
    tagline: "ASC, dialysis, and custom sites.",
    facilities: { min: 1, max: SELF_SERVE_MAX_FACILITIES },
    first_facility_cents: 5_000,
    additional_facility_cents: 4_000,
    ai_credits_per_facility: creditsForResidents(AI_CREDIT_SIZING.DIALYSIS_BEDS_PER_FACILITY),
    assistant_credits_per_facility: 125,
    residents_per_facility: AI_CREDIT_SIZING.DIALYSIS_BEDS_PER_FACILITY,
    modules: AMBULATORY_MODULES,
    portals: FAMILY_PORTALS.ambulatory,
    requiresDemo: true,
  },

  ambulatory_enterprise: {
    tier: "ambulatory_enterprise",
    family: "ambulatory",
    label: "Ambulatory Enterprise",
    tagline: "Nine or more ambulatory sites.",
    facilities: { min: SELF_SERVE_MAX_FACILITIES + 1, max: Number.POSITIVE_INFINITY },
    first_facility_cents: null,
    additional_facility_cents: null,
    ai_credits_per_facility: creditsForResidents(AI_CREDIT_SIZING.DIALYSIS_BEDS_PER_FACILITY),
    assistant_credits_per_facility: 125,
    residents_per_facility: AI_CREDIT_SIZING.DIALYSIS_BEDS_PER_FACILITY,
    modules: AMBULATORY_MODULES,
    portals: FAMILY_PORTALS.ambulatory,
    requiresDemo: true,
  },

  ltc: {
    tier: "ltc",
    family: "ltc",
    label: "Long-Term Care",
    tagline: "SNF and ALF — plus every ambulatory workflow.",
    facilities: { min: 1, max: SELF_SERVE_MAX_FACILITIES },
    first_facility_cents: 10_000,
    additional_facility_cents: 7_500,
    ai_credits_per_facility: creditsForResidents(AI_CREDIT_SIZING.LTC_BEDS_PER_FACILITY),
    assistant_credits_per_facility: 250,
    residents_per_facility: AI_CREDIT_SIZING.LTC_BEDS_PER_FACILITY,
    modules: LTC_MODULES,
    portals: FAMILY_PORTALS.ltc,
    requiresDemo: true,
  },

  ltc_enterprise: {
    tier: "ltc_enterprise",
    family: "ltc",
    label: "Long-Term Care Enterprise",
    tagline: "Nine or more facilities, health systems, and MSOs.",
    facilities: { min: SELF_SERVE_MAX_FACILITIES + 1, max: Number.POSITIVE_INFINITY },
    first_facility_cents: null,
    additional_facility_cents: null,
    ai_credits_per_facility: creditsForResidents(AI_CREDIT_SIZING.LTC_BEDS_PER_FACILITY),
    assistant_credits_per_facility: 250,
    residents_per_facility: AI_CREDIT_SIZING.LTC_BEDS_PER_FACILITY,
    modules: LTC_MODULES,
    portals: FAMILY_PORTALS.ltc,
    requiresDemo: true,
  },
};

export const PLAN_TIERS = Object.keys(PLAN_CATALOG) as PlanTier[];

/** Tiers that are sold, in the order a pricing page should list them. */
export const SELLABLE_TIERS: readonly PlanTier[] = [
  "ambulatory",
  "ambulatory_enterprise",
  "ltc",
  "ltc_enterprise",
] as const;

export function tiersForFamily(family: PlanFamily): PlanDefinition[] {
  return SELLABLE_TIERS.map((t) => PLAN_CATALOG[t]).filter((p) => p.family === family);
}

/**
 * Whether a tier can mint a Stripe Checkout Session.
 *
 * A `null` first-facility price means the amount is negotiated, so there is
 * nothing to charge. Checked BEFORE any price-id lookup: a deployment that
 * mistakenly configured a price id for a contact-us tier must still not be able
 * to sell it at whatever number that price happens to hold.
 */
export function planIsSelfServe(tier: PlanTier): boolean {
  return PLAN_CATALOG[tier].first_facility_cents !== null;
}

export const SELF_SERVE_TIERS: readonly PlanTier[] = SELLABLE_TIERS.filter(planIsSelfServe);

/**
 * Monthly total in cents for `facilityCount` facilities, or `null` when quoted.
 *
 * `first + (n - 1) × additional`. This is the arithmetic Stripe performs from
 * the graduated tier table, restated here so a page can show a total before
 * anyone reaches checkout — and so a test can prove the two agree.
 */
export function monthlyTotalCents(tier: PlanTier, facilityCount: number): number | null {
  const p = PLAN_CATALOG[tier];
  if (p.first_facility_cents === null) return null;
  const n = Number.isFinite(facilityCount) ? Math.max(1, Math.floor(facilityCount)) : 1;
  // OUTSIDE THE BAND IS NOT PRICEABLE. Asking `ltc` for nine facilities used to
  // return $700 — a specific, confident, wrong number for a count that is
  // explicitly quoted rather than listed. A surface showing it would tell a
  // nine-facility operator they could self-serve. Raised in review.
  if (!facilityCountFitsTier(tier, n)) return null;
  if (n === 1 || p.additional_facility_cents === null) return p.first_facility_cents;
  return p.first_facility_cents + (n - 1) * p.additional_facility_cents;
}

/** Annual total in cents — the monthly rate times the months charged. */
export function annualTotalCents(tier: PlanTier, facilityCount: number): number | null {
  const monthly = monthlyTotalCents(tier, facilityCount);
  return monthly === null ? null : monthly * ANNUAL_MONTHS_CHARGED;
}

/** What paying annually saves against twelve monthly payments. */
export function annualSavingCents(tier: PlanTier, facilityCount: number): number | null {
  const monthly = monthlyTotalCents(tier, facilityCount);
  if (monthly === null) return null;
  return monthly * (12 - ANNUAL_MONTHS_CHARGED);
}

export function facilityCountFitsTier(tier: PlanTier, facilityCount: number): boolean {
  const { min, max } = PLAN_CATALOG[tier].facilities;
  return facilityCount >= min && facilityCount <= max;
}

/** The tier a facility count and family land on. */
export function tierForFacilityCount(family: PlanFamily, facilityCount: number): PlanTier {
  for (const tier of tiersForFamily(family)) {
    if (facilityCountFitsTier(tier.tier, facilityCount)) return tier.tier;
  }
  return family === "ltc" ? "ltc_enterprise" : "ambulatory_enterprise";
}

/**
 * Quantity to put on the Checkout line item: the facility count.
 *
 * Stripe applies the graduated tier table to this, so the quantity IS the
 * billing input — not a clamp on it. Clamped to the tier's band because a
 * quantity outside the band would bill a count the tier does not cover; the
 * route refuses that case outright rather than relying on this.
 */
export function checkoutQuantityFor(tier: PlanTier, facilityCount: number): number {
  const { min, max } = PLAN_CATALOG[tier].facilities;
  const n = Number.isFinite(facilityCount) ? Math.floor(facilityCount) : min;
  return Math.min(max === Number.POSITIVE_INFINITY ? n : max, Math.max(min, n));
}

/**
 * The family a facility type belongs to.
 *
 * `custom` resolves to `ambulatory`, matching `FACILITY_TYPE_RULEPACK_DEFAULT`
 * in @weconsultrx/engine. Note this differs from `moduleForFacilityType`, which
 * maps `custom` to `null` — that answers "which module gates this facility",
 * and the answer there is correctly "none". This one answers "which plan does a
 * customer with this facility need", and a custom site needs a plan.
 */
export function familyForFacilityType(facilityType: string | null | undefined): PlanFamily | null {
  switch (facilityType) {
    case "snf":
    case "alf":
      return "ltc";
    case "asc":
    case "dialysis":
    case "custom":
      return "ambulatory";
    default:
      return null;
  }
}

/** One SNF anywhere in the org means the whole org needs `ltc`. */
export function familyForFacilityTypes(
  types: readonly (string | null | undefined)[],
): PlanFamily | null {
  let sawAmbulatory = false;
  for (const t of types) {
    const f = familyForFacilityType(t);
    if (f === "ltc") return "ltc";
    if (f === "ambulatory") sawAmbulatory = true;
  }
  return sawAmbulatory ? "ambulatory" : null;
}

/**
 * Which module GATES this facility type.
 *
 * MOVED HERE from apps/api/src/lib/tenant-modules.ts on 2026-08-30, and the
 * move is the point. It lived server-side while the web client carried a
 * hand-written copy of the same three-way map in `modulesForKinds`
 * (OnboardingWizardPage) and a hand-retyped `ModuleKey` union in
 * sidebar-items.ts — four statements of one fact, none importing another.
 *
 * That mattered the day the server started REFUSING an out-of-plan facility
 * type. A picker that disagrees with the gate is worse than no gate: the
 * customer is offered Skilled nursing, fills in the form, and is refused by a
 * server reading a different table. Both sides now import this one, out of the
 * package that already owns `ModuleKey`, `MODULE_KEYS` and `MODULE_INFO` —
 * which it owns because module entitlement is a PLAN fact.
 *
 * `tenant-modules.ts` re-exports it, so every existing server call site is
 * untouched.
 *
 * 'custom' and anything unrecognised map to null — never gated. Keep it that
 * way: rejectIfModuleDisabled is built on this, and a custom facility that
 * could be 409'd is the failure this null exists to prevent.
 *
 * This is NOT the same question as which modules a practice holding such a
 * facility should SEE — see visibilityModulesForFacilityType in the API, which
 * stays there because visibility is not a plan fact.
 */
export function moduleForFacilityType(
  facilityType: string | null | undefined,
): ModuleKey | null {
  switch (facilityType) {
    case "snf":
    case "alf":
      return "snf_alf";
    case "asc":
      return "asc";
    case "dialysis":
      return "dialysis";
    default:
      return null;
  }
}

/**
 * May an org whose plan grants `ceiling` operate a facility of this type?
 *
 * THE single predicate behind both the server's create/re-type gate and the
 * facility-type pickers in the onboarding wizard and the Add Facility modal.
 * One function so the offer and the refusal cannot disagree — the product
 * owner's report was exactly that disagreement in its silent form: an
 * ambulatory org was offered Skilled nursing, ticked it, was told "this turns
 * on the matching workflows", and `capToPlan` discarded the choice with no
 * signal anywhere.
 *
 * Built on the MODULE CEILING rather than on `familyForFacilityType`, though
 * that function answers a near-identical question today and would give the
 * same answers for every tier in this catalogue. Two reasons:
 *
 *   1. The ceiling is per-module, so a future tier that sells ASC without
 *      dialysis is expressible; a family is a single coarse label and would
 *      quietly permit the wrong one.
 *   2. The wizard's picker renders from the ceiling because that is what
 *      `GET /v1/tenant-settings/modules` can serve. Gating on a second,
 *      differently-derived fact is how a picker and a gate drift apart.
 *
 * `custom` and unknown types return TRUE: `moduleForFacilityType` maps them to
 * null ("never gated"), and `familyForFacilityType` puts `custom` in
 * `ambulatory`, which every tier in this catalogue includes. If a tier is ever
 * sold WITHOUT `asc`, that agreement breaks and `custom` needs its own branch
 * here — it would otherwise be the one type no plan could refuse.
 */
export function facilityTypeInPlan(
  facilityType: string | null | undefined,
  ceiling: Record<ModuleKey, boolean>,
): boolean {
  const key = moduleForFacilityType(facilityType);
  if (key === null) return true;
  return ceiling[key] === true;
}

/**
 * What every tier includes, regardless of price.
 *
 * Stated as data rather than as prose on a pricing card so the page cannot
 * drift from the product. Note what is NOT here: PORTALS. Those differ by
 * family (see FAMILY_PORTALS), and listing them as universal was true only
 * while every tier had all three.
 */
export const INCLUDED_IN_EVERY_TIER = {
  ai: ["Clinical AI synthesis", "AI assistant", "Chart Ask-AI", "AI learning loop"],
  integrations: [
    "Chart sources and ingest",
    "E-signature",
    "Calendar sync (Google + Outlook)",
    "QuickBooks + Stripe invoicing",
    "Email, SMS, and push notifications",
  ],
  compliance: [
    "Signed BAA",
    "HIPAA controls and audit log",
    "Break-glass access review",
    "Data retention controls",
  ],
} as const;

/**
 * What each legacy tier becomes.
 *
 * READ PATHS ONLY. Nothing rewrites a live row: re-pricing a paying customer is
 * a commercial decision, and doing it silently inside a deploy would change
 * what someone is charged without anyone agreeing to it.
 *
 * The band tiers collapse into their family's single tier, which grants the
 * same modules and portals — so a row on `ltc_growth` loses nothing by reading
 * as `ltc`. The pre-family tiers all map into `ltc` because it is the superset:
 * they were sold with no family distinction at all, so it is the only mapping
 * that cannot take a workflow away.
 */
export const LEGACY_TIER_MIGRATION: Record<LegacyPlanTier, PlanTier> = {
  starter: "ltc",
  pro: "ltc",
  enterprise: "ltc_enterprise",
  ambulatory_small: "ambulatory",
  ambulatory_growth: "ambulatory",
  ltc_small: "ltc",
  ltc_growth: "ltc",
};

const LEGACY_TIERS = Object.keys(LEGACY_TIER_MIGRATION) as LegacyPlanTier[];

export function isLegacyPlanTier(v: unknown): v is LegacyPlanTier {
  return typeof v === "string" && (LEGACY_TIERS as readonly string[]).includes(v);
}

export function isPlanTier(v: unknown): v is PlanTier {
  return typeof v === "string" && (PLAN_TIERS as readonly string[]).includes(v);
}

/**
 * Resolve any stored `plan_tier` — current or legacy — to a current tier.
 *
 * Falls back to `trial` for an unrecognised value rather than throwing: a row
 * carrying a tier this build has never heard of is a deployment-ordering
 * problem, and the safe response is the smallest allowance, not a 500.
 *
 * `hasOwnProperty` rather than `in`, because `"toString" in PLAN_CATALOG` is
 * true and the previous version of this lookup resolved it to a Function whose
 * limits were `undefined` — an unbounded budget from a prototype key.
 */
export function resolvePlanTier(stored: string | null | undefined): PlanTier {
  if (typeof stored !== "string") return "trial";
  if (Object.prototype.hasOwnProperty.call(PLAN_CATALOG, stored) && isPlanTier(stored)) {
    return stored;
  }
  if (isLegacyPlanTier(stored)) return LEGACY_TIER_MIGRATION[stored];
  return "trial";
}

export interface PlanLimits {
  facility_limit: number;
  resident_limit: number;
  ai_token_limit_monthly: number;
  assistant_token_limit_monthly: number;
}

/**
 * The enforced numbers for a tier at a given facility count.
 *
 * FACILITY COUNT IS AN ARGUMENT NOW. Allowances are per-facility, so a
 * two-facility tenant gets twice a one-facility tenant's budget. Defaulting to
 * 1 keeps every legacy caller honest — it under-grants rather than over-grants,
 * which is the safe direction for a value that gates spend.
 */
export function planLimits(tier: PlanTier, facilityCount = 1): PlanLimits {
  const p = PLAN_CATALOG[tier];
  const n = Number.isFinite(facilityCount) ? Math.max(1, Math.floor(facilityCount)) : 1;
  const openEnded = p.facilities.max === Number.POSITIVE_INFINITY;
  return {
    // WHAT THEY PAID FOR, not the band maximum.
    //
    // Under graduated per-facility pricing the customer buys a specific number
    // of facilities and is charged for exactly that. Granting the band maximum
    // would hand a three-facility customer five free facilities and no signal
    // that anything was owed; the band is a SALES boundary (past 8 we quote),
    // not an entitlement. Clamped to the band so a quantity Stripe should never
    // have sent cannot grant more than the tier covers.
    facility_limit: openEnded ? UNLIMITED : Math.min(n, p.facilities.max),
    resident_limit: openEnded ? UNLIMITED : p.residents_per_facility * n,
    ai_token_limit_monthly: creditsToTokens(p.ai_credits_per_facility * n),
    assistant_token_limit_monthly: creditsToTokens(p.assistant_credits_per_facility * n),
  };
}

/**
 * `PLAN_LIMITS` at ONE facility, kept as a lookup table for the call sites that
 * had one. Legacy keys resolve through `LEGACY_TIER_MIGRATION`.
 *
 * Prefer `planLimits(resolvePlanTier(x), facilityCount)` in new code — this
 * table cannot express a facility count, so it under-grants for every tenant
 * with more than one facility.
 */
export const PLAN_LIMITS: Record<AnyPlanTier, PlanLimits> = Object.freeze({
  ...(Object.fromEntries(PLAN_TIERS.map((t) => [t, planLimits(t)])) as Record<PlanTier, PlanLimits>),
  ...(Object.fromEntries(
    LEGACY_TIERS.map((t) => [t, planLimits(LEGACY_TIER_MIGRATION[t])]),
  ) as Record<LegacyPlanTier, PlanLimits>),
});

export const FACILITY_BANDS: Record<PlanTier, { min: number; max: number }> = Object.freeze(
  Object.fromEntries(PLAN_TIERS.map((t) => [t, PLAN_CATALOG[t].facilities])) as Record<
    PlanTier,
    { min: number; max: number }
  >,
);

function dollars(cents: number): string {
  // toLocaleString, not interpolation. Annual totals reach four figures and
  // rendered as "$1870" on the pricing page — a number a reader has to stop and
  // parse, on the one surface where they are comparing amounts.
  const d = cents / 100;
  return Number.isInteger(d)
    ? `$${d.toLocaleString("en-US")}`
    : `$${d.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Credits one synthesis run costs, rounded for prose.
 *
 * Derived, because the page said "about five credits" for a run that had been
 * re-measured to 6,121 tokens — a stale approximation left behind by a constant
 * change, which is the failure this whole package exists to prevent.
 */
export function creditsPerSynthesisRun(): number {
  return Math.round(TOKENS_PER_SYNTHESIS_RUN / TOKENS_PER_CREDIT);
}

/** "$100" for the first facility, or "Contact us". */
export function formatFirstFacilityPrice(tier: PlanTier): string {
  const cents = PLAN_CATALOG[tier].first_facility_cents;
  if (cents === null) return "Contact us";
  if (cents === 0) return "Free";
  return dollars(cents);
}

/** "$75" for each additional facility, or "" when there is no listed rate. */
export function formatAdditionalFacilityPrice(tier: PlanTier): string {
  const cents = PLAN_CATALOG[tier].additional_facility_cents;
  return cents === null ? "" : dollars(cents);
}

/** "$325" — the monthly total at a facility count. */
export function formatMonthlyTotal(tier: PlanTier, facilityCount: number): string {
  const cents = monthlyTotalCents(tier, facilityCount);
  return cents === null ? "Contact us" : dollars(cents);
}

/** "$3,575" — the annual total at a facility count. */
export function formatAnnualTotal(tier: PlanTier, facilityCount: number): string {
  const cents = annualTotalCents(tier, facilityCount);
  return cents === null ? "Contact us" : dollars(cents);
}

/** "1–8 facilities" / "9+ facilities". */
export function formatFacilityBand(tier: PlanTier): string {
  const { min, max } = PLAN_CATALOG[tier].facilities;
  if (max === Number.POSITIVE_INFINITY) return `${min}+ facilities`;
  if (min === max) return max === 1 ? "1 facility" : `${max} facilities`;
  return `${min}–${max} facilities`;
}

/**
 * The graduated rate as one line: "$100 then $75 each".
 *
 * Exists because three surfaces were about to phrase it three ways, and the
 * first-facility price ALONE understates what a multi-facility customer pays —
 * a picker reading "$100 · 1–8 facilities" says the wrong thing about every
 * count except one.
 */
export function formatGraduatedRate(tier: PlanTier): string {
  const first = formatFirstFacilityPrice(tier);
  const extra = formatAdditionalFacilityPrice(tier);
  if (!extra) return first;
  return `${first} then ${extra} each`;
}

/** "1,000 AI credits". */
export function formatCredits(credits: number): string {
  return `${credits.toLocaleString("en-US")} AI credits`;
}

/**
 * The facility types a module covers, as a parenthetical — or an empty string
 * when it would only repeat the label. `dialysis` is that case, and the naive
 * render produced "Dialysis (Dialysis)".
 */
export function formatModuleTypes(key: ModuleKey): string {
  const info = MODULE_INFO[key];
  return info.facilityTypes === info.label ? "" : `(${info.facilityTypes})`;
}
